import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Toaster } from './ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { FloatingChat } from './FloatingChat';
import { ForcePasswordChangeModal } from './ForcePasswordChangeModal';
import { CedulaRCModal } from './CedulaRCModal';
import { NotificationBell } from './NotificationBell';
import { getInitials, roleLabels } from '../lib/utils';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Target,
  Building2,
  Plug,
  Megaphone,
  Zap,
  BarChart3,
  Settings,
  Menu,
  Moon,
  Sun,
  LogOut,
  Bell,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  FileText,
  BookOpen,
  FolderOpen,
  Briefcase,
  CalendarDays,
  Lightbulb,
  Activity,
  Mail,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const LOGO_URL = '/estrapat-logo.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INACTIVITY_LIMIT = 4 * 60 * 60 * 1000;
const WARNING_BEFORE   = 15 * 60 * 1000;
const WARNING_AT       = INACTIVITY_LIMIT - WARNING_BEFORE;

// Menu items - Chatbot removido del sidebar, ahora es flotante. Automatizaciones eliminado.
const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'viewer', 'aspirante'] },
  { path: '/pipeline', icon: LayoutGrid, label: 'Pipeline', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante'] },
  { path: '/clients', icon: Users, label: 'Clientes', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'viewer', 'aspirante'] },
  { path: '/leads', icon: Target, label: 'Leads', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'viewer', 'aspirante'] },
  { path: '/policies', icon: FileText, label: 'Pólizas', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'viewer', 'aspirante'] },
  { path: '/inmuebles', icon: Briefcase, label: 'Soluciones Inmobiliarias', roles: ['admin', 'master'] },
  { path: '/directory', icon: BookOpen, label: 'Directorio', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante'] },
  { path: '/documents', icon: FolderOpen, label: 'Documentos', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante'] },
  { path: '/calendar', icon: CalendarDays, label: 'Calendario', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante'] },
  { path: '/agents', icon: Building2, label: 'Agentes', roles: ['admin', 'master'] },
  { path: '/users', icon: UserPlus, label: 'Usuarios', roles: ['admin', 'master'] },
  { path: '/integrations', icon: Plug, label: 'Integraciones', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante'] },
  { path: '/campaigns', icon: Megaphone, label: 'Campañas', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'aspirante'] },
  { action: 'marketing_digital', icon: Mail, label: 'Marketing Digital', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante'] },
  { path: '/reports', icon: BarChart3, label: 'Reportes', roles: ['admin', 'master', 'viewer'] },
  { path: '/sugerencias', icon: Lightbulb, label: 'Sugerencias', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'aspirante'] },
  { path: '/actividad', icon: Activity, label: 'Actividad', roles: ['admin', 'master'] },
  { path: '/errors', icon: AlertTriangle, label: 'Errores', roles: ['admin', 'master'] },
  { path: '/settings', icon: Settings, label: 'Configuración', roles: ['admin', 'master', 'agente', 'asistente', 'developer', 'asesor', 'viewer'] },
];

const CANALES_MARKETING = ['Facebook', 'Instagram', 'Google', 'Email', 'Otro'];
const EMPTY_FORM = { project_type: '', objective: '', channels: [], notes: '' };

const MarketingModal = ({ open, onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleClose = () => {
    setForm(EMPTY_FORM);
    onClose();
  };

  const toggleCanal = (canal) => {
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(canal)
        ? prev.channels.filter(c => c !== canal)
        : [...prev.channels, canal],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_type.trim() || !form.objective.trim() || form.channels.length === 0) {
      toast.error('Completa los campos requeridos y selecciona al menos un canal.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/marketing-digital/solicitar`,
        { project_type: form.project_type, objective: form.objective, channels: form.channels, notes: form.notes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Solicitud enviada correctamente');
      handleClose();
    } catch {
      toast.error('Error al enviar la solicitud. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Solicitar Marketing Digital</DialogTitle>
          <DialogDescription>
            Completa el formulario y nos pondremos en contacto contigo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="md-project">Nombre del proyecto / campaña <span className="text-red-500">*</span></Label>
            <Input
              id="md-project"
              value={form.project_type}
              onChange={e => setForm(p => ({ ...p, project_type: e.target.value }))}
              placeholder="Ej: Campaña Allianz Q3"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="md-objective">Objetivo <span className="text-red-500">*</span></Label>
            <Textarea
              id="md-objective"
              value={form.objective}
              onChange={e => setForm(p => ({ ...p, objective: e.target.value }))}
              placeholder="¿Qué quieres lograr con esta campaña?"
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Canales de interés <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-2 gap-2">
              {CANALES_MARKETING.map(canal => (
                <div key={canal} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`md-canal-${canal}`}
                    checked={form.channels.includes(canal)}
                    onChange={() => toggleCanal(canal)}
                    className="h-4 w-4 cursor-pointer accent-[#1a365d]"
                  />
                  <label htmlFor={`md-canal-${canal}`} className="text-sm cursor-pointer">{canal}</label>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="md-notes">Comentarios adicionales</Label>
            <Textarea
              id="md-notes"
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Cualquier información adicional..."
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Enviar solicitud
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { user, token } = useAuth();
  const [marketingOpen, setMarketingOpen] = useState(false);

  const userRole = user?.role || 'viewer';
  const filteredMenu = menuItems.filter(item => item.roles.includes(userRole));

  return (
    <div
      className={`hidden md:flex flex-col h-screen bg-[#1a365d] text-white transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center justify-center h-16 border-b border-white/10">
        {collapsed ? (
          <img src={LOGO_URL} alt="EstraPat" className="h-8 w-8 object-contain" />
        ) : (
          <div className="flex items-center gap-3 px-4">
            <img src={LOGO_URL} alt="EstraPat" className="h-10 w-10 object-contain" />
            <div className="flex flex-col">
              <span className="font-bold text-sm">EstraPat</span>
              <span className="text-[10px] text-white/70">Estrategias Patrimoniales</span>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1 px-2">
          {filteredMenu.map((item) => {
            if (item.action === 'marketing_digital') {
              return (
                <button
                  key="marketing_digital"
                  onClick={() => setMarketingOpen(true)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white w-full ${collapsed ? 'justify-center' : ''}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              );
            }
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center justify-center h-12 border-t border-white/10 hover:bg-white/10 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
      </button>

      <MarketingModal open={marketingOpen} onClose={() => setMarketingOpen(false)} token={token} />
    </div>
  );
};

const MobileSidebar = () => {
  const location = useLocation();
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);

  const userRole = user?.role || 'viewer';
  const filteredMenu = menuItems.filter(item => item.roles.includes(userRole));

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0 bg-[#1a365d]">
          <div className="flex items-center justify-center h-16 border-b border-white/10">
            <div className="flex items-center gap-3 px-4">
              <img src={LOGO_URL} alt="EstraPat" className="h-10 w-10 object-contain" />
              <div className="flex flex-col text-white">
                <span className="font-bold text-sm">EstraPat</span>
                <span className="text-[10px] text-white/70">Estrategias Patrimoniales</span>
              </div>
            </div>
          </div>
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {filteredMenu.map((item) => {
                if (item.action === 'marketing_digital') {
                  return (
                    <button
                      key="marketing_digital"
                      onClick={() => { setOpen(false); setMarketingOpen(true); }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white w-full"
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  );
                }
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      <MarketingModal open={marketingOpen} onClose={() => setMarketingOpen(false)} token={token} />
    </>
  );
};

const Topbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { selectedLine, currentLine, availableLines, changeBusinessLine } = useBusinessLine();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-16 border-b bg-card flex items-center justify-between px-4 md:px-6 sticky top-0 z-40 backdrop-blur-md bg-card/80">
      <div className="flex items-center gap-4">
        <MobileSidebar />
        <h1 className="text-lg font-semibold hidden sm:block">EstraPat CRM</h1>
        
        {/* Selector Global de Línea de Negocio */}
        <div className="hidden md:flex items-center">
          <Select value={selectedLine} onValueChange={changeBusinessLine}>
            <SelectTrigger className="w-[180px] h-9 border-dashed" data-testid="business-line-selector">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                <SelectValue placeholder="Línea de negocio" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {availableLines.map(line => (
                <SelectItem key={line.id} value={line.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${line.color}`}></div>
                    {line.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Mobile Business Line Selector */}
        <div className="md:hidden">
          <Select value={selectedLine} onValueChange={changeBusinessLine}>
            <SelectTrigger className="w-[140px] h-9">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${currentLine.color}`}></div>
                <span className="truncate text-xs">{currentLine.name}</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              {availableLines.map(line => (
                <SelectItem key={line.id} value={line.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${line.color}`}></div>
                    {line.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Notifications */}
        <NotificationBell />

        {/* Theme Toggle */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-[#1a365d] text-white text-xs">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium">{user?.full_name}</span>
                <Badge variant="secondary" className="text-[10px] h-4">
                  {roleLabels[user?.role] || user?.role}
                </Badge>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Mi cuenta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { user, logout } = useAuth();
  const location = useLocation();
  const resetTimerRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const timers = { inactivity: null, warning: null, countdown: null };

    const resetTimer = () => {
      clearTimeout(timers.inactivity);
      clearTimeout(timers.warning);
      clearInterval(timers.countdown);
      setShowWarning(false);

      timers.warning = setTimeout(() => {
        setShowWarning(true);
        setCountdown(15 * 60);
        timers.countdown = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timers.countdown);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, WARNING_AT);

      timers.inactivity = setTimeout(() => {
        clearInterval(timers.countdown);
        setShowWarning(false);
        logout('inactivity');
      }, INACTIVITY_LIMIT);
    };

    resetTimerRef.current = resetTimer;

    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    EVENTS.forEach(evt => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timers.inactivity);
      clearTimeout(timers.warning);
      clearInterval(timers.countdown);
      EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user, location, logout]);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {user?.force_password_change && <ForcePasswordChangeModal />}
      {['agente', 'asistente', 'developer'].includes(user?.role)
        && !user?.force_password_change
        && (!user?.cedula_vencimiento || !user?.rc_vencimiento)
        && <CedulaRCModal />}
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
      {/* Chatbot flotante - visible en todas las páginas para admin y agente */}
      <FloatingChat />
      <Toaster position="top-right" />
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 text-center">
            <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">⚠️ Sesión por expirar</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">Tu sesión se cerrará por inactividad en:</p>
            <div className="text-4xl font-bold text-orange-500 mb-6">
              {Math.floor(countdown / 60).toString().padStart(2, '0')}:{(countdown % 60).toString().padStart(2, '0')}
            </div>
            <button
              onClick={() => resetTimerRef.current?.()}
              className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors"
            >
              Continuar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
